<template>
  <ConnectorModal
    :title="initialName ? `Edit Skill: ${initialName}` : '✨ New Skill'"
    wide
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <div>
        <p class="mb-1 text-sm font-semibold">✨ AI Assist</p>
        <textarea
          v-model="aiPrompt"
          rows="3"
          class="w-full rounded-lg border border-green-700 bg-zinc-800 p-3 text-sm outline-none focus:border-green-500"
          placeholder='Describe the skill (e.g., "send an email over SMTP; host, sender and password are configuration; the LLM provides to, subject and body")'
        />
        <button
          class="mt-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-50"
          :disabled="generating || !aiPrompt.trim()"
          @click="generateWithAi"
        >
          {{ generating ? 'Generating…' : '✨ Generate with AI' }}
        </button>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Skill Name (node type)</span>
          <input
            v-model="name"
            class="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm outline-none focus:border-green-500"
            placeholder="SendEmail"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Description (LLM reads this)</span>
          <input
            v-model="description"
            class="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm outline-none focus:border-green-500"
          />
        </label>
      </div>

      <div>
        <p class="mb-1 text-sm font-medium">
          Input Schema — the AI tool contract → node inputs
        </p>
        <div class="rounded-lg border border-zinc-700">
          <div
            class="grid grid-cols-[1fr_150px_80px_60px] gap-2 border-b border-zinc-800 px-3 py-2 text-xs font-bold"
          >
            <span>Input Name</span>
            <span>Type</span>
            <span>Required</span>
            <span class="text-right">Actions</span>
          </div>
          <div
            v-for="(row, i) in inputRows"
            :key="i"
            class="grid grid-cols-[1fr_150px_80px_60px] items-center gap-2 border-b border-zinc-800 px-3 py-2"
          >
            <input
              v-model="row.name"
              placeholder="param_name"
              class="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm outline-none focus:border-green-500"
            />
            <select
              v-model="row.type"
              class="cursor-pointer rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-sm outline-none focus:border-green-500"
            >
              <option v-for="t in ROW_TYPES" :key="t" :value="t">{{ t }}</option>
            </select>
            <input
              v-model="row.required"
              type="checkbox"
              class="h-4 w-4 cursor-pointer accent-green-600"
            />
            <button
              class="justify-self-end rounded p-1 text-red-400 hover:bg-zinc-700"
              title="Delete"
              @click="inputRows.splice(i, 1)"
            >
              🗑
            </button>
          </div>
          <div class="px-3 py-2">
            <button
              class="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-500"
              @click="inputRows.push({ name: '', type: 'TEXT', required: false })"
            >
              + Add New
            </button>
          </div>
        </div>
      </div>

      <div>
        <p class="mb-1 text-sm font-medium">
          Output Schema — node outputs; skill runs standalone when wired
        </p>
        <div class="rounded-lg border border-zinc-700">
          <div
            class="grid grid-cols-[1fr_230px_60px] gap-2 border-b border-zinc-800 px-3 py-2 text-xs font-bold"
          >
            <span>Output Name</span>
            <span>Type</span>
            <span class="text-right">Actions</span>
          </div>
          <div
            class="grid grid-cols-[1fr_230px_60px] items-center gap-2 border-b border-zinc-800 px-3 py-2"
          >
            <div
              class="flex items-center rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-400"
            >
              tool <span class="ml-auto opacity-70">🔒</span>
            </div>
            <div class="flex items-center gap-2 text-xs text-zinc-500">
              <span
                class="rounded-md border border-green-700 bg-green-600/10 px-2 py-0.5 text-[10px] font-bold tracking-wide text-green-400"
              >
                MANDATORY
              </span>
              TOOL → AIToolbox
            </div>
            <span></span>
          </div>
          <div
            v-for="(row, i) in outputRows"
            :key="i"
            class="grid grid-cols-[1fr_230px_60px] items-center gap-2 border-b border-zinc-800 px-3 py-2"
          >
            <input
              v-model="row.name"
              placeholder="output_name"
              class="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm outline-none focus:border-green-500"
            />
            <select
              v-model="row.type"
              class="cursor-pointer rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-sm outline-none focus:border-green-500"
            >
              <option v-for="t in ROW_TYPES" :key="t" :value="t">{{ t }}</option>
            </select>
            <button
              class="justify-self-end rounded p-1 text-red-400 hover:bg-zinc-700"
              title="Delete"
              @click="outputRows.splice(i, 1)"
            >
              🗑
            </button>
          </div>
          <div class="px-3 py-2">
            <button
              class="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-500"
              @click="outputRows.push({ name: '', type: 'TEXT' })"
            >
              + Add New
            </button>
          </div>
        </div>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium">
          Config Schema — node inputs, settings-resolvable, hidden from the LLM
        </span>
        <div
          class="flex overflow-hidden rounded-lg border border-zinc-700 bg-zinc-950 focus-within:border-green-500"
        >
          <pre
            class="min-w-10 select-none border-r border-zinc-800 bg-white/[0.02] px-2 py-3 text-right font-mono text-xs leading-relaxed text-zinc-600"
            >{{ lineNumbers(configSchema) }}</pre
          >
          <textarea
            v-model="configSchema"
            rows="5"
            spellcheck="false"
            class="flex-1 bg-transparent p-3 font-mono text-xs leading-relaxed text-zinc-200 outline-none"
          />
        </div>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium">
          Python Code — def execute(params, config, context)
        </span>
        <div
          class="flex overflow-hidden rounded-lg border border-zinc-700 bg-zinc-950 focus-within:border-purple-500"
        >
          <pre
            class="min-w-10 select-none border-r border-zinc-800 bg-white/[0.02] px-2 py-3 text-right font-mono text-xs leading-relaxed text-zinc-600"
            >{{ lineNumbers(code) }}</pre
          >
          <textarea
            v-model="code"
            rows="14"
            spellcheck="false"
            placeholder="# Write your Python code here"
            class="flex-1 bg-transparent p-3 font-mono text-xs leading-relaxed text-green-300 outline-none placeholder:text-green-700"
          />
        </div>
      </label>

      <p v-if="error" class="text-sm text-red-400">{{ error }}</p>
      <p v-if="notice" class="text-sm text-green-400">{{ notice }}</p>
    </div>

    <template #footer>
      <button
        class="rounded-lg px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
        @click="emit('close')"
      >
        Cancel
      </button>
      <button
        class="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-50"
        :disabled="saving || !name.trim() || !code.trim()"
        @click="save"
      >
        {{ saving ? 'Saving…' : '💾 Save Skill' }}
      </button>
    </template>
  </ConnectorModal>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import ConnectorModal from '@/components/connectors/ConnectorModal.vue'
import { skillsApi } from '@/components/skills/skillsApi'
import {
  ROW_TYPES,
  rowsToSchema,
  schemaToRows,
  validateRows
} from '@/utils/jsonSchemaRows'
import type { SchemaRow } from '@/utils/jsonSchemaRows'

const { initialName = '' } = defineProps<{ initialName?: string }>()

const emit = defineEmits<{ close: []; saved: [] }>()

const aiPrompt = ref('')
const name = ref('')
const description = ref('')
const inputRows = ref<SchemaRow[]>([])
const outputRows = ref<SchemaRow[]>([{ name: 'result', type: 'TEXT' }])
const configSchema = ref('{\n  "properties": {}\n}')
const code = ref('')
const generating = ref(false)
const saving = ref(false)
const error = ref('')
const notice = ref('')

onMounted(async () => {
  if (!initialName) return
  const res = await skillsApi.get(initialName)
  if (!res.ok || !res.item) {
    error.value = res.error ?? 'Failed to load skill'
    return
  }
  name.value = res.item.name
  description.value = res.item.description
  aiPrompt.value = res.item.aiPrompt ?? ''
  configSchema.value = JSON.stringify(res.item.configSchema ?? {}, null, 2)
  inputRows.value = schemaToRows(res.item.inputSchema)
  outputRows.value = schemaToRows(res.item.outputSchema)
  code.value = normalizeCode(res.code ?? '')
})

function lineNumbers(text: string): string {
  const count = Math.max(text.split('\n').length, 1)
  return Array.from({ length: count }, (_, i) => i + 1).join('\n')
}

function normalizeCode(raw: string): string {
  if (raw.includes('\n') || !raw.includes('\\n')) return raw
  return raw
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '    ')
    .replace(/\\"/g, '"')
}

function asObject(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

async function generateWithAi() {
  error.value = ''
  notice.value = ''
  generating.value = true
  try {
    const res = await skillsApi.generate(aiPrompt.value.trim())
    if (!res.ok || !res.item) {
      error.value = res.error ?? 'Generation failed'
      return
    }
    const item = res.item
    name.value = item.name ?? name.value
    description.value = item.description ?? description.value
    if (item.config_schema)
      configSchema.value = JSON.stringify(item.config_schema, null, 2)
    const generatedInputs = asObject(item.input_schema)
    if (generatedInputs) inputRows.value = schemaToRows(generatedInputs)
    const generatedOutputs = asObject(item.output_schema)
    if (generatedOutputs) outputRows.value = schemaToRows(generatedOutputs)
    code.value = normalizeCode(item.code ?? code.value)
    notice.value = 'Generated — review the schemas and code, then Save.'
  } finally {
    generating.value = false
  }
}

function parseJsonField(label: string, text: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(text || '{}')
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object`)
  }
  return parsed as Record<string, unknown>
}

async function save() {
  error.value = ''
  notice.value = ''

  const rowError =
    validateRows(inputRows.value, 'Input Schema') ||
    validateRows(outputRows.value, 'Output Schema')
  if (rowError) {
    error.value = rowError
    return
  }

  saving.value = true
  try {
    let payload
    try {
      payload = {
        name: name.value.trim(),
        description: description.value.trim(),
        config_schema: parseJsonField('Config Schema', configSchema.value),
        input_schema: rowsToSchema(inputRows.value, true),
        output_schema: rowsToSchema(outputRows.value, false),
        ai_prompt: aiPrompt.value.trim(),
        code: code.value
      }
    } catch (ex) {
      error.value = ex instanceof Error ? ex.message : 'Invalid JSON'
      return
    }

    const res = await skillsApi.save(payload)
    if (!res.ok) {
      error.value = res.error ?? 'Save failed'
      return
    }

    notice.value = 'Saved — reloading node registry…'
    const reload = await skillsApi.reloadNodes()
    notice.value = reload.ok
      ? 'Saved. The skill node is now available in the node list.'
      : 'Saved, but node reload failed — refresh the page.'
    emit('saved')
  } finally {
    saving.value = false
  }
}
</script>
